import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors } from "../../lib/colors"
import BarChart from "../../components/analytics/BarChart"
import SummaryTable from "../../components/analytics/SummaryTable"
import DateRangePicker, { type DateRange } from "../../components/analytics/DateRangePicker"
import { readStoredRange } from "../../lib/dateRange"
import { api, type ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import PlanLockNotice from "../../components/account/PlanLockNotice"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import type { CategoryMetrics } from "../../types/analytics"
import "../../styles/analytics.scss"

const METRIC_OPTIONS = [
  { key: "avg_engagement_rate", label: "平均ENG率" },
  { key: "posts_count",         label: "投稿数" },
  { key: "avg_reach",           label: "平均リーチ" },
  { key: "avg_likes",           label: "平均いいね" },
  { key: "avg_comments",        label: "平均コメント" },
  { key: "avg_saves",           label: "平均保存" },
  { key: "avg_shares",          label: "平均シェア" },
  { key: "avg_plays",           label: "平均再生数" },
]

const SUMMARY_COLUMNS = [
  { key: "posts_count",         label: "投稿数" },
  { key: "avg_reach",           label: "平均リーチ" },
  { key: "avg_likes",           label: "平均いいね" },
  { key: "avg_comments",        label: "平均コメント" },
  { key: "avg_saves",           label: "平均保存" },
  { key: "avg_shares",          label: "平均シェア" },
  { key: "avg_engagement_rate", label: "平均ENG率" },
  { key: "avg_plays",           label: "平均再生数" },
]

const CategoriesPage = () => {
  const { selectedAccountId, selectedAccount, setSingleAccountOnly } = useSelectedAccount()
  const navigate = useNavigate()
  const barChartRef = useRef<HTMLDivElement>(null)
  const locked = selectedAccount != null && !selectedAccount.features.category

  useEffect(() => {
    setSingleAccountOnly(true)
  }, [setSingleAccountOnly])

  const [data, setData]       = useState<CategoryMetrics[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [metric, setMetric]   = useState("avg_engagement_rate")
  const [range, setRange]     = useState<DateRange>(() => readStoredRange("lyto.categoriesPeriod"))

  const fetchData = useCallback(async () => {
    if (!selectedAccountId || locked) { setLoading(false); return }
    setLoading(true)
    setForbidden(false)
    try {
      const params = new URLSearchParams({
        instagram_account_id: String(selectedAccountId),
        start_date: range.startDate,
        end_date:   range.endDate,
      })
      const res = await api.get<CategoryMetrics[]>(`/analytics/categories?${params}`)
      setData(res)
    } catch (e) {
      if ((e as ApiError).status === 403) setForbidden(true)
    } finally {
      setLoading(false)
    }
  }, [selectedAccountId, range, locked])

  useEffect(() => { fetchData() }, [fetchData])

  const sorted = [...data].sort((a, b) =>
    ((b[metric as keyof CategoryMetrics] as number) ?? 0) -
    ((a[metric as keyof CategoryMetrics] as number) ?? 0)
  )

  const barChartData = {
    labels: sorted.map(c => c.name),
    datasets: [{
      label:           METRIC_OPTIONS.find(m => m.key === metric)?.label ?? metric,
      data:            sorted.map(c => (c[metric as keyof CategoryMetrics] as number) ?? 0),
      backgroundColor: colors.primary,
    }],
  }

  const summaryRows = data.map(c => ({
    label:  c.name,
    values: {
      posts_count:         c.posts_count,
      avg_reach:           c.avg_reach,
      avg_likes:           c.avg_likes,
      avg_comments:        c.avg_comments,
      avg_saves:           c.avg_saves,
      avg_shares:          c.avg_shares,
      avg_engagement_rate: c.avg_engagement_rate,
      avg_plays:           c.avg_plays,
    },
  }))

  return (
    <AppLayout pageTitle={PAGE_TITLES["/categories"]}>
      <div className="analytics">
        {!selectedAccountId ? (
          <AccountSelectionNotice />
        ) : locked || forbidden ? (
          <PlanLockNotice message={`${PAGE_TITLES["/categories"]}はプロプラン以上でご利用いただけます`} />
        ) : (
          <>
            <div className="analytics__header">
              <DateRangePicker value={range} onChange={setRange} storageKey="lyto.categoriesPeriod" />
            </div>

            <div className="filter-tabs">
              {METRIC_OPTIONS.map(m => (
                <button
                  key={m.key}
                  className={`filter-tabs__item${metric === m.key ? " filter-tabs__item--active" : ""}`}
                  onClick={() => setMetric(m.key)}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {loading ? (
              <p style={{ color: colors.gray400 }}>読み込み中…</p>
            ) : data.length === 0 ? (
              <p style={{ color: colors.gray400 }}>カテゴリが登録されていません</p>
            ) : (
              <>
                <div className="analytics__section">
                  <div className="chart-card">
                    <div className="chart-card__header">
                      <span className="chart-card__title">カテゴリ比較</span>
                      <div className="chart-card__actions">
                        <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(barChartData), `categories_chart_${todayStr()}.csv`)}>↓ CSV</button>
                        <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(barChartRef.current, `categories_chart_${todayStr()}.png`)}>↓ PNG</button>
                      </div>
                    </div>
                    <div className="chart-card__canvas-wrap" ref={barChartRef}>
                      <BarChart data={barChartData} horizontal />
                    </div>
                  </div>
                </div>

                <div className="analytics__section">
                  <SummaryTable
                    rows={summaryRows}
                    columns={SUMMARY_COLUMNS}
                    title="カテゴリ別指標"
                    filePrefix="categories"
                    onRowClick={i => navigate(`/categories/${data[i].id}`)}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default CategoriesPage
